// @ts-check
// Iconos de trazo del rediseño (Fase 4a, REDISENO-WEB.md). Sustituyen a los
// emojis y glifos que hacían de icono (rayo, libro, papelera, flecha de
// deshacer, triángulo de ejecutar, aspa de cerrar, bombilla, señal de aviso),
// prohibidos por la guía: un emoji cambia de dibujo con el sistema operativo
// y no toma el color del texto.
//
// Todos comparten rejilla (24), trazo y color (`currentColor`: toman el del
// texto que acompañan). El grosor y la forma de los extremos los decide cada
// estilo (--icono-grosor, --icono-extremo, --icono-union en tokens.css).
//
// SIEMPRE decorativos (`aria-hidden`): el nombre accesible lo pone el texto
// del control que los lleva. Un botón que solo tenga icono necesita su propio
// `aria-label`.
import './iconos.css';

const SVG = 'http://www.w3.org/2000/svg';

/**
 * Trazos de cada icono (atributo `d` de sus `<path>`), en una rejilla de 24.
 * `relleno`: el icono se pinta relleno, no solo contorneado (el triángulo de
 * «Ejecutar» se lee mejor macizo).
 */
const ICONOS = /** @type {const} */ ({
  codigo: { trazos: ['M8 7l-5 5 5 5', 'M16 7l5 5-5 5'] },
  libro: {
    trazos: [
      'M3 5.5h6a3 3 0 0 1 3 3V20a2.5 2.5 0 0 0-2.5-2.5H3z',
      'M21 5.5h-6a3 3 0 0 0-3 3V20a2.5 2.5 0 0 1 2.5-2.5H21z',
    ],
  },
  papelera: { trazos: ['M4 7h16', 'M9 7V4h6v3', 'M6 7l1 13h10l1-13', 'M10 11v6', 'M14 11v6'] },
  deshacer: { trazos: ['M9 14L4 9l5-5', 'M4 9h10.5a5.5 5.5 0 0 1 0 11H11'] },
  ejecutar: { trazos: ['M7 4.5v15l12.5-7.5z'], relleno: true },
  descargar: { trazos: ['M12 4v11', 'M7 10l5 5 5-5', 'M4 20h16'] },
  recargar: { trazos: ['M20 11a8 8 0 1 0-2.3 5.7', 'M20 4v7h-7'] },
  buscar: { trazos: ['M10.5 4a6.5 6.5 0 1 0 0 13a6.5 6.5 0 0 0 0-13z', 'M20 20l-4.8-4.8'] },
  candado: { trazos: ['M6 11h12v9H6z', 'M8.5 11V8a3.5 3.5 0 0 1 7 0v3'] },
  check: { trazos: ['M5 12.5l4.5 4.5L19 7.5'] },
  cerrar: { trazos: ['M6 6l12 12', 'M18 6L6 18'] },
  mas: { trazos: ['M12 5v14', 'M5 12h14'] },
  menos: { trazos: ['M5 12h14'] },
  'flecha-derecha': { trazos: ['M5 12h14', 'M13 6l6 6-6 6'] },
  'flecha-izquierda': { trazos: ['M19 12H5', 'M11 6l-6 6 6 6'] },
  'flecha-abajo': { trazos: ['M6 9l6 6 6-6'] },
  consejo: {
    trazos: [
      'M9 18h6',
      'M10 21h4',
      'M12 3a6 6 0 0 0-3.6 10.8c.9.7 1.6 1.7 1.6 2.9V17h4v-.3c0-1.2.7-2.2 1.6-2.9A6 6 0 0 0 12 3z',
    ],
  },
  atencion: { trazos: ['M12 3.5L2.5 20h19z', 'M12 10v4.5', 'M12 17.2v.3'] },
  // Formas de los bloques del diagrama: identifican Diagramas, Pseudocódigo
  // y Python en Progreso, y los tipos de bloque en la paleta.
  'forma-paralelogramo': { trazos: ['M7 6h14l-4 12H3z'] },
  'forma-rectangulo': { trazos: ['M4 6h16v12H4z'] },
  'forma-rombo': { trazos: ['M12 3l9 9-9 9-9-9z'] },
  'forma-hexagono': { trazos: ['M7.5 5h9l4.5 7-4.5 7h-9L3 12z'] },
  'forma-terminal': { trazos: ['M8 6h8a6 6 0 0 1 0 12H8A6 6 0 0 1 8 6z'] },
  // Familias de premio (vitrina del perfil).
  insignia: { trazos: ['M12 2.5l8.25 4.75v9.5L12 21.5l-8.25-4.75v-9.5z'] },
  tarjeta: { trazos: ['M3 6h18v12H3z', 'M3 10h18'] },
  cubo: {
    trazos: [
      'M12 2.5l8.5 4.75v9.5L12 21.5l-8.5-4.75v-9.5z',
      'M3.5 7.25L12 12l8.5-4.75',
      'M12 12v9.5',
    ],
  },
  birrete: {
    trazos: ['M2 9.5L12 5l10 4.5L12 14z', 'M6 11.5V16c3 2.5 9 2.5 12 0v-4.5', 'M22 9.5v5'],
  },
  // Logros del perfil (uno por cada emoji del catálogo del servidor; ver
  // `ICONO_DE_LOGRO` en achievement-list.js).
  estrella: { trazos: ['M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z'] },
  diana: {
    trazos: [
      'M12 3a9 9 0 1 0 0 18a9 9 0 0 0 0-18z',
      'M12 7.5a4.5 4.5 0 1 0 0 9a4.5 4.5 0 0 0 0-9z',
      'M12 11.2v1.6',
    ],
  },
  calendario: { trazos: ['M4 6h16v14H4z', 'M4 10h16', 'M8 3.5v4', 'M16 3.5v4'] },
  triangulo: { trazos: ['M12 4l8.5 15h-17z'] },
  categorias: { trazos: ['M4 4h7v7H4z', 'M13 4h7v7h-7z', 'M4 13h7v7H4z', 'M13 13h7v7h-7z'] },
  // Las guías en diapositivas (Fase 7): una pantalla sobre su pie.
  diapositivas: { trazos: ['M3 4.5h18v11H3z', 'M12 15.5V20', 'M8 20h8'] },
});

/** @typedef {keyof typeof ICONOS} NombreIcono */

/** Nombres de todos los iconos, en el orden en que se declaran. */
export const NOMBRES_ICONOS = /** @type {readonly NombreIcono[]} */ (
  Object.freeze(Object.keys(ICONOS))
);

/**
 * Crea un icono de trazo decorativo (`aria-hidden`).
 * @param {NombreIcono} nombre
 * @param {{ clase?: string }} [opciones] - `clase`: clase(s) extra.
 * @returns {SVGSVGElement}
 */
export function crearIcono(nombre, opciones = {}) {
  const datos = /** @type {{ trazos: readonly string[], relleno?: boolean } | undefined} */ (
    Object.hasOwn(ICONOS, nombre) ? ICONOS[nombre] : undefined
  );
  if (!datos) throw new Error(`Icono desconocido: «${nombre}»`);

  const svg = /** @type {SVGSVGElement} */ (document.createElementNS(SVG, 'svg'));
  const clases = ['ui-icono'];
  if (datos.relleno) clases.push('ui-icono-relleno');
  if (opciones.clase) clases.push(opciones.clase);
  svg.setAttribute('class', clases.join(' '));
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  svg.dataset.icono = nombre;

  for (const d of datos.trazos) {
    const trazo = document.createElementNS(SVG, 'path');
    trazo.setAttribute('d', d);
    svg.append(trazo);
  }
  return svg;
}

/**
 * Pinta los iconos que marca el HTML estático (las guías, Fase 4b): cada
 * `<span data-icono="nombre">` se cambia por su icono, con las clases del
 * marcador. Así el dibujo sigue viviendo solo aquí y no se copia en el HTML.
 * Un nombre desconocido deja el marcador como estaba, sin romper la página;
 * `tests/unit/pages/guias-iconos.spec.js` vigila que no haya ninguno.
 * @param {ParentNode} [raiz] - Dónde buscar (por defecto, todo el documento).
 */
export function pintarIconos(raiz = document) {
  const nombres = /** @type {readonly string[]} */ (NOMBRES_ICONOS);
  for (const marcador of raiz.querySelectorAll('span[data-icono]')) {
    const nombre = /** @type {HTMLElement} */ (marcador).dataset.icono ?? '';
    if (!nombres.includes(nombre)) continue;
    const clase = marcador.getAttribute('class') ?? undefined;
    marcador.replaceWith(crearIcono(/** @type {NombreIcono} */ (nombre), { clase }));
  }
}
